import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useServices } from "@/hooks/useData";
import { servicePath } from "@/lib/content";
import { Reveal, EASE } from "@/components/motion/Reveal";

export function ServiceDirectory() {
  const { data: services = [] } = useServices();
  const [active, setActive] = useState(0);
  const current = services[active];

  return (
    <section id="leistungen" className="bg-paper border-t border-line" aria-labelledby="services-title">
      <div className="container-x pt-24 md:pt-32 pb-12 md:pb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <Reveal>
          <p className="label text-mute mb-6 flex items-center gap-4"><span className="w-6 h-px bg-ink/40" /> 03 · Leistungen</p>
          <h2 id="services-title" className="display text-4xl sm:text-5xl lg:text-6xl text-ink">Was wir reinigen.</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/leistungen" data-testid="services-all-link" className="label link-line inline-flex items-center gap-2 text-ink">Alle Leistungen <ArrowUpRight size={12} strokeWidth={1.5} /></Link>
        </Reveal>
      </div>

      <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-16 pb-24 md:pb-32">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <ul className="border-t border-line" onMouseLeave={() => {}}>
            {services.map((s, i) => (
              <li key={s.slug} className="border-b border-line">
                <Link
                  to={servicePath(s.slug)}
                  data-testid={`service-directory-item-${s.number}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  className="group grid grid-cols-[3rem_1fr_auto] sm:grid-cols-[4rem_1fr_auto] items-baseline gap-4 py-6 md:py-7 transition-colors duration-500"
                >
                  <span className={`label transition-colors duration-500 ${active === i ? "text-signal" : "text-mute"}`}>{s.number}</span>
                  <span className="min-w-0">
                    <span className={`display block text-2xl sm:text-3xl lg:text-4xl uppercase tracking-[-0.02em] transition-[transform,color] duration-700 ease-out ${active === i ? "translate-x-2 text-ink" : "text-ink/70"}`}>
                      {s.name}
                    </span>
                    <span className={`block text-sm text-mute mt-2 max-w-md transition-[opacity,transform] duration-700 ease-out ${active === i ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 lg:h-0 lg:overflow-hidden"} lg:block`}>
                      {s.short}
                    </span>
                  </span>
                  <ArrowUpRight size={18} strokeWidth={1.25} className={`self-center transition-all duration-500 ${active === i ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`} />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="lg:sticky lg:top-32 aspect-[4/5] sm:aspect-[16/10] lg:aspect-[4/5] relative overflow-hidden bg-stone">
            <AnimatePresence mode="popLayout" initial={false}>
              {current && (
                <motion.img
                  key={current.slug}
                  src={current.hero}
                  alt={current.name}
                  loading="lazy"
                  decoding="async"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0, scale: 1.08, clipPath: "inset(0 0 100% 0)" }}
                  animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0% 0)" }}
                  exit={{ opacity: 0, transition: { duration: 0.6 } }}
                  transition={{ duration: 1.1, ease: EASE }}
                />
              )}
            </AnimatePresence>
            {current && (
              <div className="absolute left-0 bottom-0 label text-paper/90 bg-ink/45 backdrop-blur-md px-5 py-4 flex gap-4">
                <span className="text-signal">{current.number}</span>
                <span>{current.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
